import { getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toastUtils } from "../../Utils/helper";
import Button from "./../../components/common/Button/Button";
import { CheckboxCommon } from "./../../components/common/CheckBox/CheckboxCommon";
import DataProtectionPolicyModal from "./../../components/common/Drawer/DataProtectionPolicyModal";
import TermsOfUseModal from "./../../components/common/Drawer/TermsOfUseModal";
import LoadingCommon from "./../../components/common/LoadingStatus/LoadingCommon";
import CustomInputProps from "./../../components/CustomInputProps/CustomInputProps";
import images from "./../../images";
import { useSignupMutation } from "./../../services/apiSlice";

// Constants
// const DEFAULT_COUNTRY = "SG";
const DEFAULT_COUNTRY_CODE = "+65";

const TOAST_MESSAGES = {
  OTP_SUCCESS: "toast.success.otpCodeWillExprireIn5Minutes",
  SIGNUP_FAILED: "signUp.signUpFailed",
  INVALID_PHONE: "signUp.invalidPhoneNumber",
  INVALID_AGE: "signUp.invalidAge",
  INVALID_GENDER: "signUp.invalidGender",
  AGE_OUT_OF_RANGE: "signUp.ageOutOfRange",
  VERIFYING_OTP: "toast.warning.accIsVerifyingOTPTryAgainTomorrow",
  UNEXPECTED_ERROR: "toast.error.anUnexpectedErrorOccurred",
};

// Utility Functions
const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    questId: params.get("quest_id"),
    locationId: params.get("location_id"),
    code: params.get("code"),
  };
};

const parsePhoneNumber = (phone) => {
  try {
    const phoneFirst8 = phone.slice(0, 8);

    const phoneWithCountryCode = phoneFirst8.startsWith("+")
      ? phoneFirst8
      : `${DEFAULT_COUNTRY_CODE}${phoneFirst8}`;

    const phoneNumber = parsePhoneNumberFromString(phoneWithCountryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      const country = phoneNumber.country;
      const countryCode = country ? `+${getCountryCallingCode(country)}` : "";
      const phoneWithoutCountryCode = phoneFirst8;
      return { countryCode, phoneWithoutCountryCode, isValid: true };
    }
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  } catch {
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  }
};

const handleApiError = (error, t) => {
  const status = error?.status || error?.response?.status;
  // const errorMsg =
  //   error?.data?.detail?.[0]?.msg ||
  //   error?.response?.data?.detail?.[0]?.msg ||
  //   t(TOAST_MESSAGES.SIGNUP_FAILED);

  if (status === 400) {
    return t(TOAST_MESSAGES.VERIFYING_OTP);
  }
  if (status === 422) {
    if (error?.data?.detail?.[0]?.msg === "Your number has been registered. Please Sign In.") {
      return (
        <span>
          {t("signUp.yourNumberHasBeenRegistered")}{" "}
          <Link to="/login" className="text-destructive font-bold transition-colors lowercase">
            {t("signUp.signIn")}
          </Link>
        </span>
      );
    } else {
      const fallbackMsg =
        error?.data?.detail?.[0]?.msg ||
        error?.response?.data?.detail?.[0]?.msg ||
        t(TOAST_MESSAGES.LOGIN_FAILED);

      return fallbackMsg;
    }
  }
  // if (status === 422) {
  //   // return errorMsg;
  //   return (
  //     <span>
  //       {t("signUp.yourNumberHasBeenRegistered")}{" "}
  //       <Link
  //         to="/login"
  //         className="text-[#EF4056] font-bold transition-colors lowercase"
  //       >
  //         {t("signUp.signIn")}
  //       </Link>
  //     </span>
  //   );
  // }
  return t(TOAST_MESSAGES.UNEXPECTED_ERROR);
};

const SignUpForm = ({
  signUpData,
  setSignUpData,
  onSubmit,
  isLoading,
  questId,
  locationId,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  // const isLogin = searchParams.get("login") === "true";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenTerms, setIsModalOpenTerms] = useState(false);

  const handlePhoneChange = useCallback(
    (value) => {
      const rawPhone = typeof value === "string" ? value.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhone =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const parsed = parsePhoneNumber(phoneFirst8Digits);
      setSignUpData({
        ...signUpData,
        phoneNumber: formattedPhone,
        rawPhoneNumber: phoneFirst8Digits,
        countryCode: parsed.countryCode,
        phoneWithoutCountryCode: parsed.phoneWithoutCountryCode,
        isPhoneValid: parsed.isValid,
      });
    },
    [setSignUpData, signUpData],
  );

  const handlePrivacyPolicyChange = useCallback(
    (event) => {
      setSignUpData({
        ...signUpData,
        // privacyPolicyAccepted: event.target.checked,
        privacyPolicyAccepted: event,
      });
    },
    [setSignUpData, signUpData],
  );

  const handleConsentMarketingChange = useCallback(
    (event) => {
      setSignUpData({
        ...signUpData,
        // privacyPolicyAccepted: event.target.checked,
        consentMarketing: event,
      });
    },
    [setSignUpData, signUpData],
  );

  const handleToSignIn = () => {
    if (questId || locationId) {
      navigate(
        `/sign-in?from=qrcode&quest_id=${questId}&location_id=${locationId}${code ? `&code=${code}` : ""}`,
        {
          state: { direction: -1 },
          replace: false,
        },
      );
    } else {
      navigate("/sign-in", {
        state: { direction: -1 },
        replace: false,
      });
    }
  };

  const handleAgree = () => {
    setSignUpData({ ...signUpData, privacyPolicyAccepted: true });
    setIsModalOpen(false);
  };
  const handleAgreeTermsOfUse = () => {
    setSignUpData({ ...signUpData, termsOfUseAccepted: true });
    setIsModalOpenTerms(false);
  };

  const isFormValid =
    signUpData.isPhoneValid &&
    // signUpData.isAgeValid &&
    // !!signUpData.gender &&
    signUpData.privacyPolicyAccepted;

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex flex-col gap-6">
        <label htmlFor="phone-input" className="sr-only">
          {t("signUp.phoneNumber")}
        </label>

        <div>
          <CustomInputProps
            value={signUpData.phoneNumber}
            onChange={handlePhoneChange}
            placeholder={t("signUp.whatsAppNumber")}
            iconImage={images.WhatsappIconWhite}
            countryCode={signUpData.countryCode || DEFAULT_COUNTRY_CODE}
            iconDropdown={images.DropPhoneIconGray}
          />
          {error.phone && <p className="text-destructive text-sm mt-2">{error.phone}</p>}
        </div>

        <div className="flex flex-col gap-[18px]">
          <div className="flex items-center gap-2">
            <div className="size-[30px] flex-shrink-0 flex items-center justify-center">
              <CheckboxCommon
                checked={signUpData.consentMarketing || false}
                onCheckedChange={handleConsentMarketingChange}
                id="consentToRallyCollecting-privacy-policy"
                className="rounded-[2px] size-5 bg-white border-2 border-mid-gray data-[state=checked]:bg-white data-[state=checked]:text-mid-gray"
              />
            </div>
            <label
              htmlFor="consentToRallyCollecting-privacy-policy"
              className="text-xs leading-[14px] text-mid-gray font-medium"
            >
              {t("signUp.notification")}
            </label>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-[30px] flex-shrink-0 flex items-center justify-center">
              <CheckboxCommon
                id="privacy-policy"
                checked={signUpData.privacyPolicyAccepted || false}
                onCheckedChange={handlePrivacyPolicyChange}
                className="rounded-[2px] size-5 bg-white border-2 border-mid-gray data-[state=checked]:bg-white data-[state=checked]:text-mid-gray"
              />
            </div>
            <label htmlFor="privacy-policy" className="text-xs text-mid-gray font-medium">
              <Trans i18nKey="signUp.iHaveReadAndhearby">
                <span
                  className="underline cursor-pointer"
                  onClick={() => setIsModalOpenTerms(true)}
                >
                  Terms Of Use
                </span>
                <span className="underline cursor-pointer" onClick={() => setIsModalOpen(true)}>
                  Data Protection Policy
                </span>
              </Trans>

              {error.privacyPolicy && (
                <p className="text-destructive text-sm">{error.privacyPolicy}</p>
              )}

              {/* Modal để ở ngoài Trans */}
              <TermsOfUseModal
                isOpen={isModalOpenTerms}
                onOpenChange={setIsModalOpenTerms}
                onAgree={handleAgreeTermsOfUse}
                onClose={() => setIsModalOpenTerms(false)}
              />
              <DataProtectionPolicyModal
                isOpen={isModalOpen}
                onOpenChange={setIsModalOpen}
                onAgree={handleAgree}
                onClose={() => setIsModalOpen(false)}
              />
            </label>
          </div>
        </div>
        <p className="text-sm">
          <span className="text-mid-gray">{t("signUp.alreadyHaveAccount")}</span>{" "}
          <button
            onClick={handleToSignIn}
            className="text-primary font-bold hover:underline"
            aria-label={t("base.loginHaveSpace")}
          >
            {t("base.loginHaveSpace")}
          </button>
        </p>
      </div>

      <Button
        className="h-[50px] text-[20px] font-medium text-white bg-primary rounded-md w-full mt-0"
        text={t("signUp.getWhatsAppOTP")}
        iconSub={images.WhatsappIconWhite}
        onClick={onSubmit}
        disabled={isLoading || !isFormValid}
        aria-label={t("signUp.getWhatsAppOTP")}
      />
    </div>
  );
};

// Main Component
const SignUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [register, { isLoading }] = useSignupMutation();

  const [error, setError] = useState({
    phone: "",
    privacyPolicy: "",
    isPhoneValid: false,
  });
  const [signUpData, setSignUpData] = useState({
    phoneNumber: "",
    rawPhoneNumber: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    phoneWithoutCountryCode: "",
    age: "",
    gender: "",
    isPhoneValid: false,
    isAgeValid: false,
    privacyPolicyAccepted: false,
    consentMarketing: false,
  });
  const { questId, locationId, code } = parseQueryParams(useLocation().search);

  const validateForm = (signUpData, t) => {
    const errors = {};

    if (!signUpData.isPhoneValid) {
      errors.phone = t(TOAST_MESSAGES.INVALID_PHONE);
    }

    if (!signUpData.privacyPolicyAccepted) {
      errors.privacyPolicy = t("base.required");
    }

    return errors;
  };

  const handleSubmit = useCallback(() => {
    const errors = validateForm(signUpData, t);

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setError({
      phone: "",
      age: "",
      gender: "",
      privacyPolicy: "",
    });
    const body = {
      phone: signUpData.rawPhoneNumber,
      country_code: signUpData.countryCode,
      consent_marketing: signUpData.consentMarketing,
      consent_data_protection: signUpData.privacyPolicyAccepted,
    };

    register(body)
      .unwrap()
      .then((response) => {
        if (response.message === "Operation successful" || response.status === 200) {
          toastUtils.success(t(TOAST_MESSAGES.OTP_SUCCESS));
          let navigateUrl = `/verify-otp?phone=${signUpData.phoneWithoutCountryCode}&country_code=${signUpData.countryCode}&quest_id=${questId}&location_id=${locationId}&type=signup`;

          if (code) {
            navigateUrl += `&code=${code}`;
          }

          navigate(navigateUrl);
        }
      })
      .catch((error) => {
        // toast.error(handleApiError(error, t));
        setError((prev) => ({
          ...prev,
          phone: handleApiError(error, t),
        }));
      });
  }, [signUpData, questId, locationId, register, navigate, t, code]);

  return (
    <div className="section-h-full overflow-y-auto flex flex-col px-10 max-xs:py-9 py-[72px] max-xs:gap-[30px] gap-[60px] 2xs:max-w-[430px]">
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <LoadingCommon statusLoading={isLoading} />
      <div className="flex items-center">
        <img src={images.LogoLogin} className="w-auto h-[60px]" alt="logo" />
      </div>
      <h1 className="text-4xl leading-[43px] font-bold text-primary tracking-[-0.72px]">
        {t("signUp.becomeMysteryShopper")}
      </h1>
      <p className="text-primary text-xl leading-6 font-medium">
        Your contact is used solely for authentication and cashback.
      </p>
      <div className="h-full">
        <SignUpForm
          signUpData={signUpData}
          setSignUpData={setSignUpData}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          questId={questId}
          locationId={locationId}
          error={error}
        />
      </div>
    </div>
  );
};

export default SignUpPage;
